import { useEffect, useState } from 'react';
import Dialogo from '@/components/Dialogo';
import CampoAgendamento from '@/components/leads/CampoAgendamento';
import { GRADE_SUGERIDA, chaveDia, horaDe } from '@/agenda/grade';
import { buscarAgendamentosDoDia, reagendar } from '@/services/agendamentosService';

/**
 * Remarcar uma consulta (VF-3, correção) — a PORTA que faltava.
 *
 * `reagendar()` existia no serviço desde a VF-1 e ganhou na VF-3 a limpeza da
 * confirmação, mas nenhum botão o chamava: não havia como remarcar em lugar
 * nenhum do produto.
 *
 * ★ REAPROVEITA O `CampoAgendamento` da VF-1 — a mesma grade, os mesmos avisos
 * de dia cheio, o mesmo escape "fora da grade". Um segundo seletor de horário,
 * com regras próprias, acabaria divergindo do primeiro; e "livre" tem de
 * significar a mesma coisa nas duas telas.
 *
 * PROPS-ONLY: é montado na agenda (fora do `LeadsProvider`) e no painel do lead.
 * `lead` é opcional (PC-2): a ficha passa; a agenda não tem e o serviço lê.
 */
export default function DialogoReagendar({
  aberto,
  agendamento,
  clinicaId,
  grade,
  lead = null,
  autor,
  aoFechar,
  aoConcluir,
}) {
  const [dia, setDia] = useState('');
  const [hora, setHora] = useState('');
  const [foraDaGrade, setForaDaGrade] = useState(false);
  const [agendaDoDia, setAgendaDoDia] = useState([]);
  const [carregandoAgenda, setCarregandoAgenda] = useState(false);
  const [erro, setErro] = useState(null);
  const [ocupado, setOcupado] = useState(false);

  // Abre na data atual da consulta: remarcar quase sempre é mexer perto dela.
  useEffect(() => {
    if (!aberto || !agendamento) return;
    const atual = agendamento.dataHora?.toDate?.() ?? agendamento.dataHora ?? null;
    setDia(atual ? chaveDia(atual) : chaveDia(new Date()));
    setHora('');
    setForaDaGrade(false);
    setErro(null);
  }, [aberto, agendamento]);

  useEffect(() => {
    if (!aberto || !clinicaId || !dia) return undefined;
    let vivo = true;
    setCarregandoAgenda(true);
    const [a, m, d] = dia.split('-').map(Number);
    buscarAgendamentosDoDia(clinicaId, new Date(a, m - 1, d, 12))
      .then((lista) => { if (vivo) setAgendaDoDia(lista); })
      .catch(() => { if (vivo) setAgendaDoDia([]); })
      .finally(() => { if (vivo) setCarregandoAgenda(false); });
    return () => { vivo = false; };
  }, [aberto, clinicaId, dia]);

  async function confirmar() {
    if (!dia || !hora) {
      setErro('Escolha o dia e o horário.');
      return;
    }
    setErro(null);
    setOcupado(true);
    try {
      const [a, m, d] = dia.split('-').map(Number);
      const [h, min] = hora.split(':').map(Number);
      const nova = new Date(a, m - 1, d, h, min);
      // ★ PC-2 (A2): com o lead em mãos o serviço não precisa lê-lo para decidir a transição.
      await reagendar(clinicaId, agendamento, nova, autor, lead ? { lead } : {});
      // ⚠ A DATA VAI COMO `Date`, não como texto. Quem ouve precisa gravá-la no
      // estado local da tela, e um "2026-09-12 08:30" não serve para isso —
      // `chaveDia`/`horaDe`, que decidem a coluna e a célula da grade, leem
      // Timestamp ou Date. Entregar texto foi o que deixou a caixinha parada na
      // data antiga: não havia valor pronto para usar.
      aoConcluir?.(agendamento, nova);
      aoFechar?.();
    } catch (e) {
      setErro(e.message ?? 'Não foi possível remarcar a consulta.');
    } finally {
      setOcupado(false);
    }
  }

  if (!agendamento) return null;
  const atual = agendamento.dataHora?.toDate?.() ?? agendamento.dataHora ?? null;

  return (
    <Dialogo titulo="Remarcar consulta" aberto={aberto} aoFechar={aoFechar} largura={560}>
      <div className="dialogo-corpo">
        <p className="campo-ajuda">
          {agendamento.leadNome || 'Sem nome'}
          {atual ? ` · hoje marcada para ${chaveDia(atual)} às ${horaDe(atual)}` : ''}
        </p>

        {/* ⚠ Remarcar APAGA a confirmação do paciente: ela era daquela data.
            A tela avisa antes, para a recepção saber que precisa reconfirmar. */}
        {agendamento.confirmadoEm && (
          <p className="campo-ajuda">
            Esta consulta está <strong>confirmada</strong>. Ao remarcar, a confirmação é
            desfeita — o paciente confirmou a data antiga.
          </p>
        )}

        <CampoAgendamento
          grade={grade ?? GRADE_SUGERIDA}
          agendamentosDoDia={agendaDoDia}
          dia={dia}
          aoTrocarDia={(v) => { setDia(v); setHora(''); }}
          hora={hora}
          aoEscolherHora={setHora}
          foraDaGrade={foraDaGrade}
          aoAlternarFora={(v) => { setForaDaGrade(v); setHora(''); }}
          carregando={carregandoAgenda}
        />

        {erro && <p className="erro">{erro}</p>}

        <footer className="dialogo-acoes">
          <button type="button" className="btn-secundario" disabled={ocupado} onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="btn-primario" disabled={ocupado} onClick={confirmar}>
            {ocupado ? 'Remarcando…' : 'Remarcar'}
          </button>
        </footer>
      </div>
    </Dialogo>
  );
}
